import { format } from "date-fns";

import type {
  AdvancedFilterConfig,
  AdvancedFilterDateRangeValue,
  AdvancedFilterDefinition,
  AdvancedFilterNumberRangeValue,
  AdvancedFilterOption,
  AdvancedFilterValue,
} from "./data-table-advanced-filter.types";

/**
 * Structural subset of `Column<T>` that advanced filters care about. Declared
 * here rather than importing `Column` to keep this module free of a circular
 * dependency on the DataTable component.
 */
interface AdvancedFilterColumnLike {
  key: string | number | symbol;
  label: string;
  filter?: { label?: string };
  advancedFilter?: AdvancedFilterConfig;
}

/**
 * Derives the advanced filter definitions from a column list. Shared so the
 * table and any consumer formatting the same filters for a different endpoint
 * (e.g. a PDF export) resolve keys and labels identically.
 */
export function buildAdvancedFilterDefinitions(
  columns: readonly AdvancedFilterColumnLike[],
): AdvancedFilterDefinition[] {
  return columns
    .filter((column) => column.advancedFilter)
    .map((column) => ({
      key: String(column.key),
      label:
        column.advancedFilter?.label ?? column.filter?.label ?? column.label,
      config: column.advancedFilter as AdvancedFilterConfig,
    }));
}

function isDateRangeValue(
  value: AdvancedFilterValue,
): value is AdvancedFilterDateRangeValue {
  return (
    typeof value === "object" &&
    value !== null &&
    ("startDate" in value || "endDate" in value)
  );
}

function isNumberRangeValue(
  value: AdvancedFilterValue,
): value is AdvancedFilterNumberRangeValue {
  return (
    typeof value === "object" &&
    value !== null &&
    ("min" in value || "max" in value)
  );
}

export function isActiveAdvancedFilterValue(
  value: AdvancedFilterValue | undefined,
): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === "string") return value !== "";
  if (isNumberRangeValue(value)) return Boolean(value.min || value.max);
  return Boolean(value.startDate || value.endDate);
}

export function mapAdvancedFiltersToApiParams(
  activeFilters: Record<string, AdvancedFilterValue>,
  definitions: readonly AdvancedFilterDefinition[],
): Record<string, string> {
  const params: Record<string, string> = {};

  for (const definition of definitions) {
    const value = activeFilters[definition.key];
    if (!isActiveAdvancedFilterValue(value)) continue;

    const apiField = definition.config.apiField ?? definition.key;

    if (definition.config.type === "date-range" && isDateRangeValue(value)) {
      const useDateOnlyComparison =
        definition.config.dateFilterMode === "date_gte_lte";
      const gtePrefix = useDateOnlyComparison ? "date_gte" : "gte";
      const ltePrefix = useDateOnlyComparison ? "date_lte" : "lte";

      if (value.startDate) {
        params[`${gtePrefix}[${apiField}]`] = format(
          value.startDate,
          "yyyy-MM-dd",
        );
      }
      if (value.endDate) {
        params[`${ltePrefix}[${apiField}]`] = useDateOnlyComparison
          ? format(value.endDate, "yyyy-MM-dd")
          : format(value.endDate, "yyyy-MM-dd'T'23:59:59");
      }
      continue;
    }

    if (
      definition.config.type === "number-range" &&
      isNumberRangeValue(value)
    ) {
      if (value.min) params[`gte[${apiField}]`] = value.min;
      if (value.max) params[`lte[${apiField}]`] = value.max;
      continue;
    }

    const stringValue = value as string;

    if (definition.config.type === "boolean") {
      params[`eq[${apiField}]`] = stringValue;
      continue;
    }

    if (definition.config.operator === "isNull") {
      params[`eq[${apiField}]`] = stringValue === "yes" ? "not_null" : "null";
      continue;
    }

    const operator =
      definition.config.operator ??
      (definition.config.type === "text" ? "like" : "eq");
    params[`${operator}[${apiField}]`] = stringValue;
  }

  return params;
}

export function resolveAdvancedFilterDisplayValue(
  definition: AdvancedFilterDefinition,
  value: AdvancedFilterValue,
  loadedOptions: Record<string, AdvancedFilterOption[]>,
): string {
  if (definition.config.type === "date-range" && isDateRangeValue(value)) {
    const from = value.startDate ? format(value.startDate, "dd/MM/yyyy") : "";
    const to = value.endDate ? format(value.endDate, "dd/MM/yyyy") : "";
    return [from, to].filter(Boolean).join(" — ");
  }

  if (definition.config.type === "number-range" && isNumberRangeValue(value)) {
    return [value.min, value.max].filter(Boolean).join(" — ");
  }

  if (definition.config.type === "boolean") {
    return value === "1" ? "Sim" : "Não";
  }

  if (definition.config.type === "select") {
    const options =
      definition.config.options ?? loadedOptions[definition.key] ?? [];
    return (
      options.find((option) => option.key === value)?.value ?? String(value)
    );
  }

  return String(value);
}
